'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, } from 'react';
import { useCopilot } from './context.js';
import { Slot } from './slot.js';
/**
 * Form element wired to `sendMessage(input)` on submit. Consumers compose
 * `<Copilot.InputField />` and `<Copilot.InputSubmit />` inside, or any
 * custom children.
 */
export const CopilotInput = forwardRef(function CopilotInput({ asChild, className, children, ...rest }, ref) {
    const { input, sendMessage, isLoading } = useCopilot();
    const Comp = asChild ? Slot : 'form';
    return (_jsx(Comp, { ref: ref, className: ['copilot-input', className].filter(Boolean).join(' '), onSubmit: (e) => {
            e.preventDefault();
            if (isLoading)
                return;
            sendMessage(input);
        }, ...rest, children: children }));
});
export const CopilotInputField = forwardRef(function CopilotInputField({ asChild, className, disabled, ...rest }, ref) {
    const { input, setInput, isLoading } = useCopilot();
    const Comp = asChild ? Slot : 'input';
    const innerRef = useRef(null);
    const wasLoadingRef = useRef(isLoading);
    // While a turn streams the field is disabled, which makes the browser blur
    // it; when the turn finishes and the field re-enables, focus is not restored
    // on its own, so the user has to click back in to keep typing. Return focus
    // on the streaming→idle edge — but only when nothing else grabbed it in the
    // meantime (don't yank focus away if the user moved to another field or a
    // card button while the turn ran). The disabled Send button blurs to <body>
    // on disable too, so this also covers sends made by clicking Send.
    useEffect(() => {
        const wasLoading = wasLoadingRef.current;
        wasLoadingRef.current = isLoading;
        if (!wasLoading || isLoading)
            return;
        const el = innerRef.current;
        if (!el || el.disabled)
            return;
        const active = el.ownerDocument.activeElement;
        if (!active || active === el.ownerDocument.body)
            el.focus();
    }, [isLoading]);
    const setRefs = (node) => {
        innerRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    };
    return (_jsx(Comp, { ref: setRefs, className: ['copilot-input-field', className].filter(Boolean).join(' '), value: input, onChange: (e) => setInput(e.target.value), disabled: disabled ?? isLoading, ...rest }));
});
export const CopilotInputSubmit = forwardRef(function CopilotInputSubmit({ asChild, className, disabled, type = 'submit', children, ...rest }, ref) {
    const { input, isLoading } = useCopilot();
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled ?? (isLoading || !input.trim());
    return (_jsx(Comp, { ref: ref, type: type, className: ['copilot-input-submit', className].filter(Boolean).join(' '), disabled: isDisabled, "aria-label": "Send message", ...rest, children: children }));
});
//# sourceMappingURL=input.js.map